import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  Divider,
  Fade,
  CircularProgress,
  Tooltip,
  Badge,
  LinearProgress,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import useMediaQuery from "@mui/material/useMediaQuery";

const TYPING_TIMEOUT = 2000; // ms
const SLOW_MODE_TIMEOUT = 5000; // 5 seconds
const MAX_CHARACTERS = 100;

export default function Chat({ chatId = "global", open, setOpen, unread, setUnread }) {
  const [enlarged, setEnlarged] = useState(false);
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [position, setPosition] = useState({ x: 24, y: 32 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [slowModeRemaining, setSlowModeRemaining] = useState(0);
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [userAvatars, setUserAvatars] = useState({});
  const messagesEndRef = useRef(null);
  const typingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const slowModeIntervalRef = useRef(null);
  const isMobilePortrait = useMediaQuery("(max-width:600px) and (orientation: portrait)");

  // Fetch session and profile
  useEffect(() => {
    async function fetchSessionAndProfile() {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      if (data.session?.user?.id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", data.session.user.id)
          .single();
        setProfile(profile);
      }
    }
    fetchSessionAndProfile();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single()
          .then(({ data }) => setProfile(data));
      } else {
        setProfile(null);
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Slow mode countdown effect
  useEffect(() => {
    if (slowModeRemaining > 0) {
      slowModeIntervalRef.current = setInterval(() => {
        setSlowModeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(slowModeIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (slowModeIntervalRef.current) {
        clearInterval(slowModeIntervalRef.current);
      }
    };
  }, [slowModeRemaining]);

  // Fetch messages and subscribe to realtime
  useEffect(() => {
    let subscription;
    async function fetchMessages() {
      setLoading(true);
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("chat_id", chatId)
        .order("created_at", { ascending: true })
        .limit(50);
      if (!error) setMessages(data || []);
      setLoading(false);
    }
    fetchMessages();

    // Realtime subscription for messages
    subscription = supabase
      .channel("chat_messages")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages", filter: `chat_id=eq.${chatId}` },
        (payload) => {
          setMessages((msgs) => {
            const newMsgs = [...msgs, payload.new];
            // Keep only the last 50 messages
            return newMsgs.length > 50 ? newMsgs.slice(newMsgs.length - 50) : newMsgs;
          });
          // If chat is closed, increment unread counter
          if (!open) {
            setUnread((prev) => (prev < 9 ? prev + 1 : 9));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [chatId, open]);

  // Typing indicator: listen for typing events
  useEffect(() => {
    let typingSub;
    typingSub = supabase
      .channel(`chat_typing_${chatId}`)
      .on(
        "broadcast",
        { event: "typing" },
        (payload) => {
          if (!profile || payload.payload.username === profile.username) return;
          setTypingUsers((users) => {
            const now = Date.now();
            // Remove expired
            const filtered = users.filter(
              (u) => u.username !== payload.payload.username && now - u.last < TYPING_TIMEOUT
            );
            // Add or update this user
            return [
              ...filtered,
              { username: payload.payload.username, last: now },
            ];
          });
        }
      )
      .subscribe();

    // Clean up typing users after timeout
    const interval = setInterval(() => {
      setTypingUsers((users) =>
        users.filter((u) => Date.now() - u.last < TYPING_TIMEOUT)
      );
    }, 500);

    return () => {
      supabase.removeChannel(typingSub);
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [chatId, profile?.username]);

  // Scroll to bottom on new message or open
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setUnread(0); // Reset unread when opened
    }
  }, [messages, open]);

  // Send typing event
  const sendTyping = () => {
    if (!profile?.username) return;
    if (typingRef.current) return;
    typingRef.current = true;
    supabase
      .channel(`chat_typing_${chatId}`)
      .send({
        type: "broadcast",
        event: "typing",
        payload: { username: profile.username },
      });
    typingTimeoutRef.current = setTimeout(() => {
      typingRef.current = false;
    }, TYPING_TIMEOUT / 2);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setInput(value);
      sendTyping();
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !session?.user?.id || !profile?.username) return;
    
    // Check slow mode
    const now = Date.now();
    const timeSinceLastMessage = now - lastMessageTime;
    if (timeSinceLastMessage < SLOW_MODE_TIMEOUT) {
      const remainingTime = Math.ceil((SLOW_MODE_TIMEOUT - timeSinceLastMessage) / 1000);
      setSlowModeRemaining(remainingTime);
      return;
    }
    
    await supabase.from("chat_messages").insert([
      {
        chat_id: chatId,
        user_id: session.user.id,
        username: profile.username,
        content: input.trim(),
      },
    ]);
    setInput("");
    setLastMessageTime(now);
  };

  // Avatar fallback: first letter of username
  const getAvatar = (username) =>
    username ? username.charAt(0).toUpperCase() : "?";

  // Fetch user avatar
  const fetchUserAvatar = async (username) => {
    if (userAvatars[username]) return userAvatars[username];
    
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("username", username)
        .single();
      
      if (!error && data) {
        setUserAvatars(prev => ({
          ...prev,
          [username]: data.avatar_url
        }));
        return data.avatar_url;
      }
    } catch (error) {
      console.error("Error fetching avatar:", error);
    }
    return null;
  };

  // Add a third size option (normal, enlarged, extra enlarged)
  const [sizeIndex, setSizeIndex] = useState(0);
  const sizes = [
    { width: 340, height: 440 },
    { width: 340 * 1.3, height: 440 * 1.3 },
    { width: 340 * 1.6, height: 440 * 1.6 }, // 60% larger
  ];
  const width = sizes[sizeIndex].width;
  const height = sizes[sizeIndex].height;

  // Update enlarge/reduce button to cycle through sizes
  const handleCycleSize = () => setSizeIndex((i) => (i + 1) % sizes.length);

  // Typing indicator text
  const typingDisplay =
    typingUsers.length === 1
      ? `${typingUsers[0].username} is typing...`
      : typingUsers.length > 1
      ? "Someone is typing..."
      : "";

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.closest('[data-chat-window]').getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Constrain to viewport
    const maxX = window.innerWidth - width;
    const maxY = window.innerHeight - height;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Drag and drop handling
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, width, height]);

  // Check if user can send message
  const canSendMessage = session && input.trim() && slowModeRemaining === 0;

  // Fetch avatars for all unique usernames in current messages
  useEffect(() => {
    const uniqueUsernames = [...new Set(messages.map(msg => msg.username))];
    uniqueUsernames.forEach(username => {
      if (!userAvatars[username]) {
        fetchUserAvatar(username);
      }
    });
  }, [messages]);

  return (
    <>
      {/* Chat Toggle Button */}
      <Box
        sx={{
          position: "fixed",
          left: isMobilePortrait ? 12 : 24, // small gap from left on mobile
          bottom: isMobilePortrait ? 12 : 32, // small gap from bottom on mobile
          zIndex: 1300,
        }}
      >
        <Fade in={!open}>
          <Badge
            badgeContent={unread > 9 ? "9+" : unread}
            color="warning"
            invisible={unread === 0}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: 700,
                fontSize: 18,
                minWidth: 28,
                height: 28,
                right: -8,
                top: 8,
                background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                color: "#181818",
                border: "2px solid #181818",
                boxShadow: "0 2px 8px rgba(244, 147, 48, 0.4)",
              },
            }}
          >
            <IconButton
              color="primary"
              sx={{
                background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                border: "2px solid #f49330",
                width: isMobilePortrait ? 63 : 84,   // 0.75x size for mobile
                height: isMobilePortrait ? 63 : 84,  // 0.75x size for mobile
                "&:hover": { 
                  background: "linear-gradient(135deg, #2e2e2e 0%, #3a3a3a 100%)",
                  borderColor: "#fff176",
                  boxShadow: "0 8px 24px rgba(244, 147, 48, 0.3)",
                  transform: "translateY(-2px)",
                },
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                setOpen(true);
                setUnread(0);
              }}
              size="large"
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: isMobilePortrait ? 36 : 48, color: "#f49330" }} />
            </IconButton>
          </Badge>
        </Fade>
      </Box>
      {/* Chat Box */}
      <Fade in={open}>
        <Paper
          elevation={8}
          data-chat-window
          sx={{
            position: "fixed",
            left: isMobilePortrait ? "2.5vw" : position.x,
            bottom: isMobilePortrait ? 16 : window.innerHeight - position.y - height,
            width: isMobilePortrait ? "95vw" : width,
            height: isMobilePortrait ? "80vh" : height,
            maxWidth: isMobilePortrait ? "95vw" : undefined,
            maxHeight: isMobilePortrait ? "80vh" : "90vh",
            zIndex: 1400,
            display: open ? "flex" : "none",
            flexDirection: "column",
            borderRadius: isMobilePortrait ? 3 : 4,
            background: "rgba(35,35,35,0.25)", // glass effect, not a gradient
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            color: "#fff",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            overflow: "hidden",
            transition: isDragging ? "none" : "width 0.2s, height 0.2s",
            border: "2px solid #3e3e3e",
            cursor: isMobilePortrait ? "default" : (isDragging ? "grabbing" : "default"),
          }}
        >
          {/* Header */}
          <Box
            onMouseDown={isMobilePortrait ? undefined : handleMouseDown}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
              borderBottom: "2px solid #f49330",
              cursor: isMobilePortrait ? "default" : "grab",
              "&:active": {
                cursor: isMobilePortrait ? "default" : "grabbing",
              },
              userSelect: "none",
            }}
          >
            <Typography sx={{ 
              fontWeight: 700, 
              fontSize: 20,
              background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              Live Chat
            </Typography>
            <Box>
              {!isMobilePortrait && (
                <Tooltip
                  title={
                    sizeIndex === 0
                      ? "Enlarge"
                      : sizeIndex === 1
                      ? "Super Enlarge"
                      : "Reduce"
                  }
                >
                  <IconButton
                    onClick={handleCycleSize}
                    sx={{ 
                      color: "#f49330", 
                      mr: 1,
                      "&:hover": {
                        color: "#fff176",
                        background: "rgba(244, 147, 48, 0.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                    size="small"
                  >
                    {sizeIndex === 2 ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={() => setOpen(false)}
                sx={{ 
                  color: "#f49330",
                  "&:hover": {
                    color: "#fff176",
                    background: "rgba(244, 147, 48, 0.1)",
                  },
                  transition: "all 0.3s ease",
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1,
              background: "transparent", // remove gradient here
              // Custom scrollbar
              "&::-webkit-scrollbar": {
                width: 8,
              },
              "&::-webkit-scrollbar-track": {
                background: "#1a1a1a",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#f49330",
                borderRadius: 4,
                "&:hover": {
                  background: "#fff176",
                },
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress size={32} sx={{ color: "#f49330" }} />
              </Box>
            ) : (
              <List dense>
                {messages.map((msg) => (
                  <ListItem
                    key={msg.id}
                    alignItems="flex-start"
                    sx={{
                      mb: 1,
                      px: 0,
                      py: 1,
                      alignItems: "flex-end",
                      background: "rgba(244, 147, 48, 0.05)",
                      borderRadius: 2,
                      border: "1px solid rgba(244, 147, 48, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(244, 147, 48, 0.08)",
                        borderColor: "rgba(244, 147, 48, 0.2)",
                      },
                    }}
                  >
                    <Avatar
                      src={userAvatars[msg.username] || ""}
                      sx={{
                        width: 36,
                        height: 36,
                        background: !userAvatars[msg.username] 
                          ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                          : "transparent",
                        color: "#181818",
                        fontWeight: 700,
                        fontSize: 16,
                        mr: 2,
                        ml: 1,
                        border: "2px solid #444",
                        boxShadow: "0 2px 8px rgba(244, 147, 48, 0.3)",
                      }}
                    >
                      {!userAvatars[msg.username] && getAvatar(msg.username)}
                    </Avatar>
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#f49330",
                          }}
                          component="span"
                        >
                          {msg.username}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#999",
                            fontWeight: 500,
                          }}
                          component="span"
                        >
                          {new Date(msg.created_at).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 15,
                          color: "#e0e0e0",
                          wordBreak: "break-word",
                          lineHeight: 1.4,
                        }}
                      >
                        {msg.content}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
                <div ref={messagesEndRef} />
              </List>
            )}
            {/* Typing indicator */}
            {typingDisplay && (
              <Box
                sx={{
                  background: "rgba(99, 102, 241, 0.1)",
                  border: "1px solid rgba(99, 102, 241, 0.3)",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mt: 1,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontStyle: "italic",
                    color: "#6366f1",
                    fontSize: 14,
                  }}
                >
                  {typingDisplay}
                </Typography>
              </Box>
            )}
          </Box>
          <Divider sx={{ borderColor: "#444" }} />
          {/* Input */}
          <Box
            component="form"
            onSubmit={handleSend}
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 3,
              py: 2,
              background: "transparent", // remove gradient here
            }}
          >
            {/* Character count and slow mode indicator */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: input.length >= MAX_CHARACTERS * 0.8 ? "#ff6b6b" : "#999",
                  fontWeight: 500,
                }}
              >
                {input.length}/{MAX_CHARACTERS}
              </Typography>
              {slowModeRemaining > 0 && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "#ff6b6b",
                    fontWeight: 500,
                  }}
                >
                  Slow mode: {slowModeRemaining}s
                </Typography>
              )}
            </Box>
            {/* Slow mode progress bar */}
            {slowModeRemaining > 0 && (
              <LinearProgress
                variant="determinate"
                value={((SLOW_MODE_TIMEOUT / 1000 - slowModeRemaining) / (SLOW_MODE_TIMEOUT / 1000)) * 100}
                sx={{
                  mb: 2,
                  height: 4,
                  borderRadius: 2,
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                  },
                  background: "rgba(244, 147, 48, 0.2)",
                }}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder={
                  session
                    ? slowModeRemaining > 0
                      ? `Wait ${slowModeRemaining}s before sending...`
                      : "Type your message…"
                    : "Login to chat"
                }
                value={input}
                onChange={handleInputChange}
                disabled={!session || slowModeRemaining > 0}
                sx={{
                  flex: 1,
                  mr: 2,
                  "& .MuiInputBase-root": {
                    color: "#fff",
                    background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                    borderRadius: 3,
                    fontSize: 15,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#555",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#f49330",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#f49330",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#999",
                  },
                  "& .Mui-disabled": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#333",
                    },
                  },
                }}
                autoComplete="off"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!canSendMessage}
                sx={{
                  fontWeight: 700,
                  borderRadius: 3,
                  minWidth: 0,
                  px: 3,
                  py: 1.5,
                  background: canSendMessage 
                    ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                    : "#444",
                  color: canSendMessage ? "#181818" : "#999",
                  fontSize: 14,
                  boxShadow: canSendMessage ? "0 4px 16px rgba(244, 147, 48, 0.3)" : "none",
                  "&:hover": canSendMessage ? { 
                    background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                    boxShadow: "0 6px 20px rgba(244, 147, 48, 0.4)",
                    transform: "translateY(-1px)",
                  } : {},
                  "&:disabled": {
                    background: "#444",
                    color: "#999",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {slowModeRemaining > 0 ? slowModeRemaining : "Send"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}